import { kitDateParts, kitWords, type KitTextLocale } from '../kitText';
import { buildStory, type BuildStoryInput, type StoryCountdownDays } from './storyCopy';
import type { KitStory } from './storyTypes';
import {
  inCubic,
  nearestInRange,
  nudge,
  outBack,
  outCubic,
  progress,
  round3,
  clamp01,
  type VideoBeatGrid,
} from './videoMotion';

/**
 * The countdown videos (design `asset-redesign/o Countdown - new`, owner answers 2026-10-08): one
 * plan makes the three files (7, 3 and 1 days). Act 1 is the countdown Story still at 0.88 in
 * motion, with the day number as the hero from frame 0. Act 2, from the downbeat near 60 % of the
 * length, is the end card: "Out Friday" ("Out tomorrow" one day before), the cover, the date as the
 * hero, the title and the artist. The CTA pill and the SmartLink line stay in one place from the
 * third beat to the end. Code writes every text and places every item (`storyLayout`, format
 * `video916`); the director gives only the song window and the cover crop.
 */

/** The timing of a countdown video, in seconds of the video. */
export interface CountdownVideoTiming {
  /** The cover row of act 1 rises into place (the second beat). */
  rowInSec: number;
  /** The CTA group comes in (the third beat) and stays to the end. */
  ctaInSec: number;
  /** The cut to the end card: the downbeat nearest to 60 % of the length. */
  cutSec: number;
  /** The pill nudges on these downbeats (the cut, then every downbeat up to 1 s before the end). */
  nudgeSecs: number[];
  /** The length of one bar of the window: the Retro record of the end card turns once per bar. Absent: 2 s (120 bpm). */
  barSec?: number;
}

/** The texts and the timing of one countdown video (`plan.short`, kind `countdown`). */
export interface KitCountdownVideo {
  kind: 'countdown';
  /** Act 1: the countdown Story (moment `countdown`, format `video916`). */
  count: KitStory;
  /** Act 2: the end card (the pre-save Story layout with the end kicker and the date as the hero). */
  end: KitStory;
  timing: CountdownVideoTiming;
  /** The poster of the file: the full still of act 1, after the CTA is in (1.2 s). */
  posterSec: number;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The English copy of the end card (owner answer 2 of page o); the other languages are in `../kitText.ts`. */
export const COUNTDOWN_VIDEO_COPY = {
  outOn: (weekday: string): string => `Out ${weekday}`,
  outTomorrow: 'Out tomorrow',
} as const;

/** The poster of a countdown video: frame 36 at 30 fps, the full still (owner answer 5 of page o). */
export const COUNTDOWN_POSTER_SEC = 1.2;
/** The share of the length where the end card starts (design o: 9.18 s of 15 s). */
const CUT_SHARE = 0.6;
/** No new motion in the last second: the last second holds. */
const HOLD_LAST_SEC = 1;

/** "Friday" for `2026-10-16`; null for a date that the code cannot read. Pure. */
export function releaseWeekdayName(releaseDate: string | null): string | null {
  const match = releaseDate === null ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(releaseDate);
  if (match === null) {
    return null;
  }
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
  return WEEKDAY_NAMES[day] ?? null;
}

/** The kicker of the end card: "Out tomorrow" one day before, else "Out <weekday>". Null with no date. Pure. */
export function countdownEndKicker(
  days: StoryCountdownDays,
  releaseDate: string | null,
  locale?: KitTextLocale | null
): string | null {
  const words = kitWords(locale);
  if (days === 1) {
    return releaseDate === null ? null : words.outTomorrow;
  }
  const parts = kitDateParts(releaseDate);
  return parts === null ? null : (words.outOnWeekday[parts.weekday] ?? null);
}

/**
 * The two Stories of a countdown video: act 1 (the countdown Story) and the end card (the pre-save
 * Story with the end kicker), both in the `video916` frame. They share the pill and the SmartLink
 * line, so the CTA group does not move at the cut. Pure.
 */
export function buildCountdownVideoStories(
  input: Omit<BuildStoryInput, 'format' | 'moment' | 'countdownDays' | 'lyric'> & { days: StoryCountdownDays }
): { count: KitStory; end: KitStory } {
  const { days, ...rest } = input;
  const count = buildStory({ ...rest, format: 'video916', moment: 'countdown', countdownDays: days });
  const preSave = buildStory({ ...rest, format: 'video916', moment: 'preSave' });
  return {
    count,
    end: { ...preSave, kicker: countdownEndKicker(days, input.releaseDate, input.locale), cta: count.cta },
  };
}

/**
 * The timing of a countdown video from the beat grid of its window (design o, "Code and director":
 * a fixed table in beats, not seconds). With a grid that does not give a beat, the times of the
 * 120 bpm grid. Pure.
 */
export function countdownVideoTiming(grid: VideoBeatGrid, lengthSec: number): CountdownVideoTiming {
  const beatNear = (index: number, fallback: number): number => {
    const beat = grid.beats[index];
    return beat !== undefined && Math.abs(beat - fallback) <= fallback * 0.6 ? beat : fallback;
  };
  const target = lengthSec * CUT_SHARE;
  const cutSec =
    nearestInRange(grid.downbeats, target, lengthSec * 0.5, lengthSec * 0.7) ?? round3(Math.round(target * 2) / 2);
  const nudgeSecs = [
    cutSec,
    ...grid.downbeats.filter((beat) => beat > cutSec + 0.3 && beat < lengthSec - HOLD_LAST_SEC),
  ];
  return { rowInSec: beatNear(1, 0.45), ctaInSec: beatNear(2, 0.89), cutSec, nudgeSecs, barSec: barSecOf(grid) };
}

/** The median gap of the downbeats of a grid (one bar), else 2 s. Pure. */
function barSecOf(grid: VideoBeatGrid): number {
  const gaps = grid.downbeats
    .slice(1)
    .map((beat, index) => beat - (grid.downbeats[index] ?? beat))
    .filter((gap) => gap > 0.5 && gap < 6)
    .sort((a, b) => a - b);
  const middle = gaps[Math.floor(gaps.length / 2)];
  return middle === undefined ? DEFAULT_BAR_SEC : round3(middle);
}

/** One bar at 120 bpm. */
const DEFAULT_BAR_SEC = 2;
/** The Neon tube flickers for this long at the cut (design o: 2 frames, never more). */
const NEON_FLICKER_SEC = 2 / 30;
/** The Handmade marker line under the title draws in over this long from the cut (design o). */
const MARKER_DRAW_SEC = 0.4;

/** The pose of one item at a time: opacity, a vertical move in px, a scale. */
export interface VideoItemPose {
  opacity: number;
  y: number;
  scale: number;
}

/** The poses of the items of a countdown video at one time. */
export interface CountdownVideoPose {
  /** Act 1 as a whole (it fades out over the cut). */
  count: VideoItemPose;
  /** The day number: it settles from 92 % in the first 0.35 s, grows with the kick, leaves upward at the cut. */
  number: VideoItemPose;
  /** The cover row of act 1: it rises 40 px into place. */
  row: VideoItemPose;
  /** Act 2 as a whole (hidden before the cut). */
  end: VideoItemPose;
  endKicker: VideoItemPose;
  endCover: VideoItemPose;
  /** The date: it rises in and grows a little with the kick. */
  endHero: VideoItemPose;
  endId: VideoItemPose;
  /** The CTA group (pill, SmartLink line, watermark): grows from 90 % at the third beat. */
  cta: VideoItemPose;
  /** The pill inside the CTA group: it nudges on the downbeats from the cut. */
  pill: VideoItemPose;
  /** The haze behind act 1 and the end card: 0.35 + 0.65 energy. */
  haze: number;
  /** True for the 2 frames of the Neon tube flicker at the cut (design o). */
  flicker: boolean;
  /** The turn of the Retro record of the end card: one turn per bar from the cut (design o). */
  recordTurnDeg: number;
  /** The Handmade marker line under the end-card title: 0 to 1 over 0.4 s from the cut (design o). */
  markerDraw: number;
}

const POSE: VideoItemPose = { opacity: 1, y: 0, scale: 1 };

/**
 * The pose of every item of a countdown video at the time `t` (design o, `VideoFrame` k-count).
 * `kick` and `energy` are the song signals of the frame (0..1). The last second holds: no move
 * starts there. Pure.
 */
export function countdownVideoPose(
  t: number,
  timing: CountdownVideoTiming,
  signals: { kick: number; energy: number },
  options: { maxNumberScale?: number } = {}
): CountdownVideoPose {
  const { kick, energy } = signals;
  const maxNumberScale = options.maxNumberScale ?? Number.POSITIVE_INFINITY;
  const barSec = timing.barSec ?? DEFAULT_BAR_SEC;
  const cut = timing.cutSec;
  const settle = t < 0.35 ? 0.92 + 0.08 * outBack(progress(t, 0, 0.35)) : 1;
  const out = outCubic(progress(t, cut, cut + 0.3));
  const row = outCubic(progress(t, timing.rowInSec, timing.rowInSec + 0.35));
  const endOn = t >= cut + 0.12;
  const coverIn = outCubic(progress(t, cut + 0.12, cut + 0.57));
  const kickerIn = outCubic(progress(t, cut + 0.12, cut + 0.42));
  const dateIn = outCubic(progress(t, cut + 0.3, cut + 0.65));
  const idIn = outCubic(progress(t, cut + 0.45, cut + 0.8));
  const cta = outBack(progress(t, timing.ctaInSec, timing.ctaInSec + 0.36));
  const pill = timing.nudgeSecs.reduce((sum, at) => sum + nudge(t, at), 1);
  return {
    count: { opacity: round3(1 - out), y: 0, scale: 1 },
    number: {
      opacity: 1,
      y: round3(-90 * inCubic(progress(t, cut, cut + 0.3))),
      // The number grows up from its foot; the room above it in the column caps the kick (y 270 on).
      scale: round3(Math.min(settle * (1 + 0.035 * kick), Math.max(1, maxNumberScale))),
    },
    row: { opacity: round3(row), y: round3(40 * (1 - row)), scale: 1 },
    end: { ...POSE, opacity: endOn ? 1 : 0 },
    endKicker: { opacity: round3(kickerIn), y: round3(-30 * (1 - kickerIn)), scale: 1 },
    endCover: { opacity: round3(coverIn), y: 0, scale: round3(0.6 + 0.4 * coverIn) },
    endHero: { opacity: round3(dateIn), y: round3(50 * (1 - dateIn)), scale: round3(1 + 0.03 * kick * dateIn) },
    endId: { opacity: round3(idIn), y: round3(30 * (1 - idIn)), scale: 1 },
    // Back-out for the timing, capped at 100 %, so the group never grows past the safe column.
    cta: { opacity: round3(clamp01(cta * 1.4)), y: 0, scale: round3(Math.min(1, 0.9 + 0.1 * cta)) },
    pill: { opacity: 1, y: 0, scale: round3(pill) },
    haze: round3(0.35 + 0.65 * clamp01(energy)),
    flicker: t >= cut && t < cut + NEON_FLICKER_SEC,
    recordTurnDeg: t < cut ? 0 : round3((360 * (t - cut)) / barSec),
    markerDraw: round3(outCubic(progress(t, cut + 0.45, cut + 0.45 + MARKER_DRAW_SEC))),
  };
}
